import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigation } from '../contexts/NavigationContext';
import { Shield, Smartphone, Key, CheckCircle, Loader, Copy } from 'lucide-react';
import { useTranslation } from '../i18n/hooks/useTranslation';

export const FirstTime2FASetup: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const { navigate } = useNavigation();
  const { t } = useTranslation();
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const isNavigatingAway = useRef(false);

  const [setupMethod, setSetupMethod] = useState<'authenticator' | 'sms' | null>(null);
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [manualEntryKey, setManualEntryKey] = useState<string | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [smsCode, setSmsCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [step, setStep] = useState<'choose' | 'setup' | 'verify'>('choose');

  useEffect(() => {
    // Redirect if user already has 2FA enabled (but not if we're navigating away)
    if (user && !user.needs2FASetup && !isNavigatingAway.current) {
      navigate('/');
    }
  }, [user, navigate]);

  const handleSetupAuthenticator = async () => {
    setIsLoading(true);
    setMessage('');
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/auth/2fa/setup/authenticator`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        setQrCode(data.qrCode);
        setManualEntryKey(data.manualEntryKey);
        setSetupMethod('authenticator');
        setStep('verify');
      } else {
        setMessage(data.message || 'Failed to setup authenticator app');
      }
    } catch (error: any) {
      setMessage(error.message || 'Failed to setup authenticator app');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetupSMS = async () => {
    if (!phoneNumber) {
      setMessage('Please enter your phone number');
      return;
    }

    setIsLoading(true);
    setMessage('');
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/auth/2fa/setup/sms`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ phoneNumber })
      });

      const data = await response.json();
      if (data.success) {
        setSetupMethod('sms');
        setStep('verify');
        setMessage('Verification code sent to your phone');
      } else {
        setMessage(data.message || 'Failed to send SMS code');
      }
    } catch (error: any) {
      setMessage(error.message || 'Failed to send SMS code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyAuthenticator = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!verificationCode || verificationCode.length !== 6) {
      setMessage('Please enter a valid 6-digit code');
      return;
    }

    setIsLoading(true);
    setMessage('');
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/auth/2fa/verify/authenticator`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ code: verificationCode })
      });

      const data = await response.json();
      if (data.success) {
        setBackupCodes(data.backupCodes || []);
        setShowBackupCodes(true);
        setVerificationCode('');
        await refreshUser();
      } else {
        setMessage(data.message || 'Invalid verification code');
      }
    } catch (error: any) {
      setMessage(error.message || 'Failed to verify code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifySMS = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!smsCode || smsCode.length !== 6) {
      setMessage('Please enter a valid 6-digit code');
      return;
    }

    setIsLoading(true);
    setMessage('');
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/auth/2fa/verify/sms`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ code: smsCode })
      });

      const data = await response.json();
      if (data.success) {
        setBackupCodes(data.backupCodes || []);
        setShowBackupCodes(true);
        setSmsCode('');
        setPhoneNumber('');
        await refreshUser();
      } else {
        setMessage(data.message || 'Invalid verification code');
      }
    } catch (error: any) {
      setMessage(error.message || 'Failed to verify code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleContinue = async () => {
    setIsLoading(true);
    // Mark that we're navigating away to prevent useEffect from redirecting
    isNavigatingAway.current = true;
    
    try {
      // Refresh user state to ensure needs2FASetup is updated
      await refreshUser();
      
      // Small delay to ensure state is propagated
      await new Promise(resolve => setTimeout(resolve, 200));
    } catch (error) {
      console.error('Error refreshing user:', error);
      // Continue navigation even if refresh fails - backend should have updated the flag
    } finally {
      setIsLoading(false);
    }
    
    // If user has set up at least one method, allow them to continue
    // They can set up the other method later in Profile settings
    navigate('/');
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setMessage('Copied to clipboard!');
    setTimeout(() => setMessage(''), 2000);
  };

  if (showBackupCodes) {
    return (
      <>
        {/* Backdrop overlay - blocks entire app */}
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9998]" />
        
        {/* Modal */}
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-8 shadow-lg max-w-md w-full">
          <div className="text-center mb-6">
            <CheckCircle className="w-16 h-16 mx-auto mb-4 text-success-600" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              {t('admin.twoFactor.enabledSuccess') || '2FA Enabled Successfully!'}
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              {t('admin.twoFactor.saveBackupCodes') || 'Please save these backup codes in a safe place. You can use them to access your account if you lose your device.'}
            </p>
          </div>

          <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg mb-4">
            <div className="grid grid-cols-2 gap-2">
              {backupCodes.map((code, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-2 bg-white dark:bg-gray-600 rounded border border-gray-200 dark:border-gray-500"
                >
                  <code className="text-sm font-mono text-gray-900 dark:text-gray-100">{code}</code>
                  <button
                    onClick={() => copyToClipboard(code)}
                    className="ml-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                    title="Copy"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <button
            type="button"
            onClick={handleContinue}
            className="w-full bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader className="w-5 h-5 animate-spin inline mr-2" />
                {t('common.loading') || 'Loading...'}
              </>
            ) : (
              t('common.continue') || 'Continue'
            )}
          </button>
          </div>
        </div>
      </>
    );
  }

  if (step === 'verify' && setupMethod === 'authenticator') {
    return (
      <>
        {/* Backdrop overlay - blocks entire app */}
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9998]" />
        
        {/* Modal */}
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-8 shadow-lg max-w-md w-full">
          <div className="text-center mb-6">
            <Shield className="w-16 h-16 mx-auto mb-4 text-primary-600" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              {t('admin.twoFactor.setupAuthenticator') || 'Setup Authenticator App'}
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              {t('admin.twoFactor.scanQRCode') || 'Scan the QR code with your authenticator app, then enter the verification code.'}
            </p>
          </div>

          {qrCode && (
            <div className="mb-6 text-center">
              <img src={qrCode} alt="QR Code" className="mx-auto mb-4" />
              {manualEntryKey && (
                <div className="mt-4">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                    {t('admin.twoFactor.cantScan') || "Can't scan? Enter this code manually:"}
                  </p>
                  <div className="flex items-center justify-center space-x-2">
                    <code className="text-sm font-mono bg-gray-100 dark:bg-gray-700 px-3 py-2 rounded">
                      {manualEntryKey}
                    </code>
                    <button
                      onClick={() => copyToClipboard(manualEntryKey)}
                      className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                      title="Copy"
                    >
                      <Copy className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          <form onSubmit={handleVerifyAuthenticator} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('admin.twoFactor.verificationCode') || 'Verification Code'}
              </label>
              <input
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"
                placeholder="000000"
                maxLength={6}
                required
              />
            </div>

            {message && (
              <div className={`p-3 rounded-md text-sm ${
                message.includes('success') || message.includes('Copied')
                  ? 'bg-success-100 text-success-700 dark:bg-success-900 dark:text-success-300'
                  : 'bg-error-100 text-error-700 dark:bg-error-900 dark:text-error-300'
              }`}>
                {message}
              </div>
            )}

            <div className="flex space-x-3">
              <button
                type="button"
                onClick={() => {
                  setStep('choose');
                  setSetupMethod(null);
                  setQrCode(null);
                  setManualEntryKey(null);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                {t('common.back') || 'Back'}
              </button>
              <button
                type="submit"
                disabled={isLoading || verificationCode.length !== 6}
                className="flex-1 bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 flex items-center justify-center"
              >
                {isLoading ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin mr-2" />
                    {t('common.verifying') || 'Verifying...'}
                  </>
                ) : (
                  t('common.verify') || 'Verify'
                )}
              </button>
            </div>
          </form>
          </div>
        </div>
      </>
    );
  }

  if (step === 'verify' && setupMethod === 'sms') {
    return (
      <>
        {/* Backdrop overlay - blocks entire app */}
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9998]" />
        
        {/* Modal */}
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-8 shadow-lg max-w-md w-full">
          <div className="text-center mb-6">
            <Smartphone className="w-16 h-16 mx-auto mb-4 text-primary-600" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              {t('admin.twoFactor.setupSMS') || 'Setup SMS 2FA'}
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              {t('admin.twoFactor.enterSMSCode') || 'Enter the verification code sent to your phone.'}
            </p>
          </div>

          <form onSubmit={handleVerifySMS} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('admin.twoFactor.verificationCode') || 'Verification Code'}
              </label>
              <input
                type="text"
                value={smsCode}
                onChange={(e) => setSmsCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"
                placeholder="000000"
                maxLength={6}
                required
              />
            </div>

            {message && (
              <div className={`p-3 rounded-md text-sm ${
                message.includes('success') || message.includes('sent')
                  ? 'bg-success-100 text-success-700 dark:bg-success-900 dark:text-success-300'
                  : 'bg-error-100 text-error-700 dark:bg-error-900 dark:text-error-300'
              }`}>
                {message}
              </div>
            )}

            <div className="flex space-x-3">
              <button
                type="button"
                onClick={() => {
                  setStep('choose');
                  setSetupMethod(null);
                  setSmsCode('');
                }}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                {t('common.back') || 'Back'}
              </button>
              <button
                type="submit"
                disabled={isLoading || smsCode.length !== 6}
                className="flex-1 bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 flex items-center justify-center"
              >
                {isLoading ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin mr-2" />
                    {t('common.verifying') || 'Verifying...'}
                  </>
                ) : (
                  t('common.verify') || 'Verify'
                )}
              </button>
            </div>
          </form>
          </div>
        </div>
      </>
    );
  }

  // Choose method step
  return (
    <>
      {/* Backdrop overlay - blocks entire app */}
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9998]" />
      
      {/* Modal */}
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-8 shadow-lg max-w-md w-full">
        <div className="text-center mb-8">
          <Shield className="w-16 h-16 mx-auto mb-4 text-primary-600" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            {t('admin.twoFactor.setup2FA') || 'Setup Two-Factor Authentication'}
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            {t('admin.twoFactor.chooseMethod') || 'Choose a method to secure your account. You can set up both methods if you want.'}
          </p>
        </div>

        <div className="space-y-4">
          {/* Authenticator Option */}
          <button
            onClick={handleSetupAuthenticator}
            disabled={isLoading}
            className="w-full p-4 border-2 border-gray-300 dark:border-gray-600 rounded-lg hover:border-primary-500 dark:hover:border-primary-500 transition-colors text-left disabled:opacity-50"
          >
            <div className="flex items-center space-x-3">
              <Key className="w-8 h-8 text-primary-600" />
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                  {t('admin.twoFactor.authenticatorApp') || 'Authenticator App'}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('admin.twoFactor.useAuthenticatorApp') || 'Use an app like Google Authenticator or Authy'}
                </p>
              </div>
            </div>
          </button>

          {/* SMS Option */}
          <div className="p-4 border-2 border-gray-300 dark:border-gray-600 rounded-lg">
            <div className="flex items-center space-x-3 mb-3">
              <Smartphone className="w-8 h-8 text-primary-600" />
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                  {t('admin.twoFactor.sms') || 'SMS'}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('admin.twoFactor.receiveCodesViaSMS') || 'Receive codes via text message'}
                </p>
              </div>
            </div>
            <div className="space-y-3">
              <input
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"
                placeholder={t('admin.twoFactor.phoneNumber') || '+1234567890'}
              />
              <button
                onClick={handleSetupSMS}
                disabled={isLoading || !phoneNumber}
                className="w-full bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50"
              >
                {isLoading ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin inline mr-2" />
                    {t('common.sending') || 'Sending...'}
                  </>
                ) : (
                  t('admin.twoFactor.sendCode') || 'Send Code'
                )}
              </button>
            </div>
          </div>
        </div>

        {message && (
          <div className={`mt-4 p-3 rounded-md text-sm ${
            message.includes('success') || message.includes('sent')
              ? 'bg-success-100 text-success-700 dark:bg-success-900 dark:text-success-300'
              : 'bg-error-100 text-error-700 dark:bg-error-900 dark:text-error-300'
          }`}>
            {message}
          </div>
        )}

        <div className="mt-6 text-center">
          <button
            onClick={handleContinue}
            className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
          >
            {t('common.skipForNow') || 'Skip for now (you can set this up later)'}
          </button>
        </div>
        </div>
      </div>
    </>
  );
};

